import { SectionType } from '@/api/gql/graphql';
import {
  Button,
  MenuDropdown,
  SearchBar,
  SelectDropDown,
  TextField,
} from '@/components/Common';
import {
  IconPosition,
  TextfieldType,
} from '@/components/Common/TextField/TextField.types';
import { Modal } from '@/components/Layout';
import { Header } from '@/components/Layout/Header/Header';
import { ListItem, Tabs, TextEditor } from '@/components/Ui';
import { useModule } from './module.hook';
import addModule from '@/assets/icons/addModule.svg';
import { Divider } from 'primereact/divider';
import plusIcon from '@/assets/icons/plus.svg';
import HighlightsForm from './HighlightsForm';
import { useHighlightStore } from '@/store/Highlight/HighlightStore';
import { Controller } from 'react-hook-form';
import { AlertModal } from '@/components/Layout/AlertModal/AlertModal';
import Highlights from './Highlights';

const Module = () => {
  const {
    register,
    errors,
    registerNewTab,
    setValueNewTab,
    errorsNewTab,
    handleChangeSectionType,
    handleAddTab,
    handleDraft,
    tabs,
    activeTab,
    setActiveTab,
    isModalOpen,
    handleCloseModal,
    highlights,
    searchHighlights,
    setSearchHighlights,
    menuItems,
    control,
    handleEditorContentChange,
    params,
    handleUpdateSection,
    handleDeleteTab,
    handleEditTab,
    editingTab,
    setIsModalOpen,
    isModalConfirmationOpen,
    setIsModalConfirmationOpen,
    handleValidateAndPublish,
    isUpdateSectionPending,
    section,
    handleConfirmAction,
    handlePublish,
    modalTitle,
    modalMessage,
    buttonTitle,
  } = useModule();
  const { openHighlightModal } = useHighlightStore();

  const currentTabContent = tabs[activeTab]?.content;

  return (
    <>
      <div className="p-4 md:p-0">
        <Header userName="Mas Aly" role="Admin" title="Nouveau Module" goBack />
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(600px,_1.3fr)_auto_1fr] gap-4 items-start">
          <form>
            <div>
              <div className="mt-9 flex flex-col md:flex-row justify-start relative w-full md:w-[363px] h-[65px]">
                <div className="bg-[#1D1D1D] rounded-[5.16px] w-full md:w-[60px] h-[55px] flex items-center justify-center border-[1.165px] border-[#2F2E2E]">
                  <span className="text-white text-xl font-semibold pl-3">
                    #
                  </span>
                  <TextField
                    type={TextfieldType.INPUT}
                    textFieldType={TextfieldType.INPUT}
                    name="position"
                    register={register('position')}
                    helpText={errors.position?.message}
                    errorStyle="text-red-500 text-xs !font-normal absolute -bottom-[15px] left-0"
                    className="h-[55px] placeholder:text-white placeholder:text-center text-center -ml-2 p-0"
                  />
                </div>
                <TextField
                  placeholder="Titre du module"
                  type={TextfieldType.INPUT}
                  textFieldType={TextfieldType.INPUT}
                  name="title"
                  register={register('title')}
                  helpText={errors.title?.message}
                  errorStyle="text-red-500 text-xs text-center !font-normal ml-[70px]"
                  className="ml-5 !bg-[#1D1D1D] w-full md:w-[283px] h-[55px] !border-[1.165px] !border-[#2F2E2E]"
                />
              </div>
              <div className="flex items-start mb-11 mt-9 relative">
                <div className="overflow-x-auto max-w-[calc(100%-40px)]">
                  <Tabs
                    activeIndex={activeTab}
                    setActiveIndex={setActiveTab}
                    tabs={tabs}
                    tabsStyle="flex whitespace-nowrap"
                    onDelete={handleDeleteTab}
                    onEdit={handleEditTab}
                    showAction={true}
                  />
                </div>
                <Button
                  className="ml-4 !bg-transparent !p-0 hover:!bg-transparent size-8 shrink-0"
                  icon={<img src={addModule} alt="addModule" />}
                  onClick={() => setIsModalOpen(true)}
                />
              </div>

              {errors.content && (
                <p className="text-red-500 text-xs mb-1">
                  {errors.content?.message}
                </p>
              )}
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <TextEditor
                    value={currentTabContent}
                    backgroundColor="#000"
                    onChange={(content, editor) =>
                      handleEditorContentChange(content, editor, field.onChange)
                    }
                  />
                )}
              />
              <div className="flex justify-between w-full my-8">
                <Button
                  type="submit"
                  title={
                    isUpdateSectionPending ? 'Enregistrer...' : 'Enregistrer'
                  }
                  className="bg-white text-black"
                  onClick={params.moduleId ? handleUpdateSection : handleDraft}
                />

                <Button
                  type="button"
                  title={section?.isPublished ? 'Cacher' : 'Publier'}
                  className="w-[160px]"
                  onClick={handleValidateAndPublish}
                />
              </div>
            </div>
            <AlertModal
              isOpen={isModalConfirmationOpen}
              onClose={() => setIsModalConfirmationOpen(false)}
              handleConfirm={
                params?.moduleId ? handleConfirmAction : handlePublish
              }
              title={modalTitle}
              message={modalMessage}
              buttonTitle={buttonTitle}
              buttonCancelTitle="Non"
            />
          </form>

          <div className="flex justify-center items-center h-[710px] mt-36">
            <Divider
              layout="vertical"
              className="bg-[#292932] w-[1px] h-[718px] mx-8"
            />
          </div>

          <Highlights
            highlights={highlights}
            searchHighlights={searchHighlights}
            setSearchHighlights={setSearchHighlights}
            menuItems={menuItems}
            openHighlightModal={openHighlightModal}
            className="mt-36"
          />
        </div>
        <HighlightsForm selectedHighlight={null} />
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          headerStyle="bg-[#2A2A2A] h-0 p-0 m-0"
          modalStyle="bg-[#2A2A2A] w-[375px]"
        >
          <div className="p-4">
            <h2 className="text-white text-xl text-center mb-9">
              Type de contenu
            </h2>
            <SelectDropDown
              placeholder="Type de contenu"
              options={Object.values(SectionType).map((type) => ({
                _id: type,
                name: type,
              }))}
              name="type"
              handleChange={handleChangeSectionType}
              setValue={setValueNewTab}
              initialSelectedOptions={
                editingTab
                  ? { _id: editingTab.type, name: editingTab.type }
                  : undefined
              }
              helpText={errorsNewTab.type?.message}
              errorStyle="text-red-500 text-xs"
              className="w-full mb-4 bg-[#232323]"
              selectDropDownStyle={{ paddingLeft: '5px' }}
            />
            <TextField
              placeholder="Nom de l'onglet"
              textFieldType={TextfieldType.INPUT}
              className="!bg-[#232323] w-full mt-2 placeholder:text-[#ADADB1] placeholder:text-sm"
              name="newTabName"
              register={registerNewTab('newTabName')}
              helpText={errorsNewTab.newTabName?.message}
              errorStyle="text-red-500 text-xs"
            />
            <div className="flex w-full mt-11">
              <Button
                title={editingTab ? 'Modifier' : 'Ajouter'}
                className="w-full"
                onClick={handleAddTab}
              />
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default Module;
